/* eslint-disable no-shadow */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import styled from 'styled-components';
import classNames from 'classnames';

import {
   setActiveExercise,
   clearActiveExercise,
   clearTutorial,
} from '../app/actions';
import { ExerciseControls } from './ExerciseControls';
import { Tutorial } from '../tutorial/Tutorial';
import { Interface } from '../interfaces/Interface';
import { ExerciseTitle } from './ExerciseTitle';
import { ExerciseLoading } from './ExerciseLoading';
import { exerciseStages } from '../shared/constants';
import { MinimalHeader } from '../routes/Dashboard';

const ExerciseWrapper = styled.div`
   padding: 0 10px var(--exercise-bottom-padding);
   --below-interface-height-px: 55px;

   @media (min-height: 500px) {
      --below-interface-height-px: 70px;
   }

   @media (min-height: 700px) {
      --below-interface-height-px: 86px;
   }

   --below-interface-height: max(
      calc(var(--below-interface-height-px) - var(--safe-area-inset-bottom)),
      55px
   );

   &.tutorial-active {
      --below-interface-height: var(--tutorial-height);
   }
`;

const mapStateToProps = ({ activeExercise, tutorial }) => ({
   showExerciseControls: !!activeExercise,
   tutorialActive: !!tutorial || !!activeExercise?.prompt,
});

const mapDispatchToProps = {
   setActiveExercise,
   clearActiveExercise,
   clearTutorial,
};

const Exercise = connect(
   mapStateToProps,
   mapDispatchToProps
)(
   ({
      setActiveExercise,
      clearActiveExercise,
      clearTutorial,
      tutorialActive,
   }) => {
      const { exerciseId, moduleId } = useParams();
      const { search } = useLocation();
      const [ready, setReady] = useState(false);

      const { levelSelect, nextConfig } = useMemo(() => {
         const returnObj = {};

         if (!search) {
            return returnObj;
         }
         const searchParams = new URLSearchParams(search);
         const level = searchParams.get('level');

         if (level) {
            returnObj.levelSelect = {
               level: Number(level),
               tutorial: searchParams.has('tutorial'),
            };
         }

         const next = searchParams.get('next');

         if (next) {
            returnObj.nextConfig = {
               path: next,
               remainingSessionCount:
                  Number(searchParams.get('session-count')) || 0,
            };
         }

         return returnObj;
      }, [search]);

      useEffect(() => {
         setActiveExercise(exerciseId, moduleId, levelSelect, nextConfig);
         setReady(true);
         return () => {
            clearActiveExercise();
            clearTutorial();
         };
      }, [
         exerciseId,
         moduleId,
         levelSelect,
         setActiveExercise,
         clearActiveExercise,
         clearTutorial,
      ]);

      if (!ready) {
         return null;
      }

      return (
         <>
            <ExerciseWrapper
               data-interface-wrapper
               className={classNames({
                  'tutorial-active': tutorialActive,
               })}
            >
               <MinimalHeader />
               <ExerciseTitle />
               <Interface />
            </ExerciseWrapper>
            <ExerciseControls />
            <Tutorial hideForStage={exerciseStages.viewScore} />
            <ExerciseLoading />
         </>
      );
   }
);

export { Exercise, ExerciseWrapper };
